import { useTranslation } from 'react-i18next';
import Kpi from '../ui/Kpi';
import { formatCurrency } from '../../utils/format';
import type { ServerLine } from './profitabilityModel';

const money = (n: number) => formatCurrency(n);

/**
 * P-34 (D-184, D-188) - the "שרת" line of a profitability screen: what the customer pays for the
 * server, what it costs OTE, and the profit (price - cost). Draws nothing when the figures hold no server.
 */
export default function ServerFigures({ line, as = 'kpi' }: { line: ServerLine | null; as?: 'kpi' | 'inline' }) {
  const { t } = useTranslation();
  if (!line) return null;
  const items = [
    { key: 'revenue', value: line.revenue, negative: false },
    { key: 'cost', value: line.cost, negative: false },
    { key: 'profit', value: line.profit, negative: line.profit < 0 },
  ];
  if (as === 'inline') {
    return (
      <dl className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm" data-server-line="">
        <dt className="font-medium text-text">{t('profitability.server.title')}</dt>
        {items.map((i) => (
          <div key={i.key} className="flex items-baseline gap-2">
            <dt className="text-text-2">{t(`profitability.server.${i.key}`)}</dt>
            <dd className={`font-semibold tabular-nums ${i.negative ? 'text-danger-700' : 'text-text'}`}>{money(i.value)}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3" data-server-line="">
      {items.map((i) => (
        <Kpi key={i.key} label={`${t('profitability.server.title')}: ${t(`profitability.server.${i.key}`)}`} value={money(i.value)} tone={i.negative ? 'danger' : undefined} />
      ))}
    </div>
  );
}
